import { profileSchema } from "@lib/auth/shared/profileSchema";
import { useCurrentUser } from "@client/hooks/use-current-user";
import {
  isIPadOffline,
  OFFLINE_CACHE_KEYS,
  readOfflineCache,
} from "@client/offline/offlineCache";
import { resolveOfflineUser } from "@client/offline/resolveOfflineUser";
import { type ReactNode } from "react";
import { Login } from ".";
import { OfflineSignInGate } from "./OfflineSignInGate";
import { CompleteProfile } from "./CompleteProfile";

type CurrentUser = NonNullable<ReturnType<typeof useCurrentUser>["data"]>;

export function AuthWrapper({
  children,
  loading = null,
  renderFallback = (content) => content,
}: {
  children: ReactNode;
  loading?: ReactNode;
  renderFallback?: (content: ReactNode) => ReactNode;
}) {
  const { data: user, isLoading } = useCurrentUser();

  if (isLoading) {
    return renderFallback(loading);
  }

  // On the iPad app while offline, fall back to the user cached during a
  // previous online session instead of bouncing to the login screen. On
  // desktop/iPhone this resolves to the normal login behavior.
  const resolution = resolveOfflineUser<CurrentUser>({
    user,
    cachedUser: readOfflineCache<CurrentUser>(OFFLINE_CACHE_KEYS.currentUser),
    iPadOffline: isIPadOffline(),
  });

  if (resolution.kind === "offline-first-launch") {
    return renderFallback(<OfflineSignInGate />);
  }

  if (resolution.kind === "login") {
    return renderFallback(<Login />);
  }

  if (!profileSchema.safeParse({ name: resolution.user.userName }).success) {
    return renderFallback(<CompleteProfile />);
  }

  return children;
}
